import { action, CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'
import grupoService from 'src/modules/grupo/grupo.service'
import IntegracaoMonitoramentoKeys from '../integracao-monitoramento.keys'
import integracaoMonitoramentoService from '../integracao-monitoramento.service'
import type { MonitoramentoRow } from '../integracao-monitoramento.types'
import { IntegracaoMonitoramentoFormKeys } from './integracao-monitoramento_form.keys'
import IntegracaoMonitoramentoFormScope from './integracao-monitoramento_form.scopes'

const LOG = Logger.get('IntegracaoMonitoramentoFormPresenter')

export class IntegracaoMonitoramentoFormPresenter extends CubePresenter<
    MainPresenter,
    IntegracaoMonitoramentoFormScope
> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new IntegracaoMonitoramentoFormScope())
    }

    // :: Instance
    #monitoramentoId?: number

    // :: Methods

    buscarMonitoramentoSelecionado = async (monitoramentoId?: number) => {
        if (!monitoramentoId) return

        const monitoramento = await integracaoMonitoramentoService.fetchMonitoramentoInfo(monitoramentoId)
        if (monitoramento) {
            this.scope.onFormDataChange(monitoramento)
            this.#monitoramentoId = monitoramentoId
        }
    }

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new IntegracaoMonitoramentoFormKeys(this.app, intent)

        keys.parentSlot(this.scope)

        LOG.debug(`applyParameters ${keys.monitoramentoId}`)
        if (initialization) {
            await this.#initialize()
            await this.#loadGruposAtivos()
        }

        if (this.#monitoramentoId !== keys.monitoramentoId) {
            await this.buscarMonitoramentoSelecionado(keys.monitoramentoId)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new IntegracaoMonitoramentoFormKeys(this.app, intent)
        keys.monitoramentoId = this.#monitoramentoId
        LOG.debug(`publishParameters ${keys.monitoramentoId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.handleClose.bind(this)

        this.scope.onSave = this.handleSave.bind(this)

        this.scope.onFormDataChange = this.handleFormDataChange.bind(this)
    }

    @action()
    private async handleClose() {
        await new IntegracaoMonitoramentoKeys(this.app).flip()
    }

    @action()
    private async handleSave() {
        try {
            if (!this.scope.formData.doc) {
                throw Error('Documento deve ser informado')
            }
            if (!this.scope.formData.tipo) {
                throw Error('Tipo deve ser informado')
            }

            if (this.scope.formData.tipo !== 'MONITORADO_VISAO_CEDENTE') {
                this.scope.formData.grupo_proc_id = undefined
            }

            const retSalvar = this.scope.formData.id
                ? await integracaoMonitoramentoService.updateMonitoramento(this.scope.formData.id, this.scope.formData)
                : await integracaoMonitoramentoService.saveMonitoramento(this.scope.formData)

            if (retSalvar) {
                const target = new IntegracaoMonitoramentoKeys(this.app)

                await target.flip()
                this.app.notify('success', 'Salvo com sucesso')
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }

    async #loadGruposAtivos() {
        LOG.debug('loadGruposAtivos')
        const grupos = await grupoService.fetchGruposAtivos()
        this.scope.gruposAtivos = grupos
    }

    @action()
    private async handleFormDataChange(dadosAlterados: Partial<MonitoramentoRow>) {
        this.scope.formData = {
            ...this.scope.formData,
            ...dadosAlterados
        }
    }
}

export default IntegracaoMonitoramentoFormPresenter
